import PropTypes from 'prop-types';
import React, {Component} from 'react';
import {connect} from 'react-redux';
import {bindActionCreators} from 'redux';
import cyberSourceActions from '../../../actions/cybersource/browser';
import {
    getCountryCode,
    getCsrf,
    getTranslations,
    getLanguageCode,
    getAvailableLanguageCodes,
    getWebRelaunchStyleEnabled,
    getCalculation,
    getCustomer
} from '../../../utils/selectors';
import {CyberSourceForm, PaymentFormTemplate} from '../../organisms';
import {areVendorsReady, getSubmittingStatus} from './cybersource.selectors';
import localiseUrl from '../../../utils/localiseUrl';
import config from '../../../config';

import {CARD_LOGOS, COUNTRY_CODES} from '../../../consts';

class CyberSource extends Component {
    getPaymentMethods() {
        const {customer} = this.props;
        const paymentMethods = ['visa', 'mastercard', 'maestro'];

        if (customer.countryCode === COUNTRY_CODES.USA) {
            paymentMethods.push('discover');
        }

        return paymentMethods.map(name => ({
            name,
            logo: CARD_LOGOS[name]
        }));
    }

    getTitle() {
        const {translations} = this.props;
        const cardType = this.getCardType();

        if (cardType === 'debit') {
            return 'Pay with debit card';
        }

        if (cardType === 'credit') {
            return 'Pay with credit card';
        }

        return translations.formHeader.payMethod;
    }

    getCardType() {
        const {location} = this.props;

        if (config.features.binTableChecking.enabled === false) {
            return null;
        }

        const queryParams = new URLSearchParams(location.search);
        const cardType = queryParams.get('cardType');

        if (cardType === 'debit') {
            return 'debit';
        }

        if (cardType === 'credit') {
            return 'credit';
        }

        return null;
    }

    render() {
        const {
            actions: {
                startAuthorization
            },
            isLoaded,
            isSubmitting,
            genericError,
            cyberSourceData,
            csrfToken,
            cancellationUrl,
            translations,
            languageCode,
            availableLanguageCodes,
            featureFlags,
            isWebRelaunchStyleEnabled,
            calculation,
            setCybersourceVisibility,
            hasSavedCreditCards,
            location
        } = this.props;

        const title = this.getTitle();

        return (
            <div className="cybersource-container">
                <PaymentFormTemplate
                    translations={translations}
                    cancellationUrl={cancellationUrl}
                    paymentMethods={this.getPaymentMethods()}
                    title={title}
                    payWithSavedCard={translations.formHeader.payWithSavedCard}
                    setCybersourceVisibility={setCybersourceVisibility}
                    hasSavedCreditCards={hasSavedCreditCards}
                    location={location}
                >
                    <CyberSourceForm
                        cyberSourceData={cyberSourceData}
                        isLoaded={isLoaded}
                        isSubmitting={isSubmitting}
                        translations={translations}
                        startAuthorization={startAuthorization}
                        csrfToken={csrfToken}
                        genericError={genericError}
                        languageCode={languageCode}
                        availableLanguageCodes={availableLanguageCodes}
                        featureFlags={featureFlags}
                        calculation={calculation}
                        cancellationUrl={cancellationUrl}
                        isWebRelaunchStyleEnabled={isWebRelaunchStyleEnabled}
                        cardType={this.getCardType()}
                    />
                </PaymentFormTemplate>
            </div>
        );
    }
}

CyberSource.propTypes = {
    actions: PropTypes.shape({
        startAuthorization: PropTypes.func
    }).isRequired,
    countryCode: PropTypes.string.isRequired,
    cyberSourceData: PropTypes.shape().isRequired,
    translations: PropTypes.shape().isRequired,
    csrfToken: PropTypes.string.isRequired,
    cancellationUrl: PropTypes.string,
    availableLanguageCodes: PropTypes.arrayOf(PropTypes.string).isRequired,
    featureFlags: PropTypes.shape().isRequired,
    isWebRelaunchStyleEnabled: PropTypes.bool.isRequired,
    genericError: PropTypes.bool.isRequired,
    calculation: PropTypes.shape({
        price: PropTypes.shape({
            fees: PropTypes.number,
            sendAmount: PropTypes.number.isRequired,
            sendCurrencyCode: PropTypes.string.isRequired
        }).isRequired
    }),
    setCybersourceVisibility: PropTypes.func.isRequired,
    hasSavedCreditCards: PropTypes.bool,
    location: PropTypes.shape({
        pathname: PropTypes.string.isRequired,
        search: PropTypes.string.isRequired
    }).isRequired
};

CyberSource.defaultProps = {
    cancellationUrl: null,
    calculation: null,
    hasSavedCreditCards: false
};

const mapStateToProps = state => {
    const languageCode = getLanguageCode(state);
    const availableLanguageCodes = getAvailableLanguageCodes(state);
    const {cancellationUrl} = state.cyberSourceData.data;

    return {
        countryCode: getCountryCode(state),
        cyberSourceData: state.cyberSourceData,
        translations: getTranslations(state),
        csrfToken: getCsrf(state),
        languageCode,
        cancellationUrl: localiseUrl(cancellationUrl, languageCode, availableLanguageCodes),
        isSubmitting: getSubmittingStatus(state),
        isLoaded: areVendorsReady(state),
        availableLanguageCodes,
        featureFlags: state.featureFlags,
        isWebRelaunchStyleEnabled: getWebRelaunchStyleEnabled(state),
        calculation: getCalculation(state),
        customer: getCustomer(state)
    };
};

const mapDispatchToProps = dispatch => ({
    actions: bindActionCreators(cyberSourceActions, dispatch)
});

export const ConnectedCyberSource = connect(mapStateToProps, mapDispatchToProps)(CyberSource);

export default ConnectedCyberSource;
